// Payment System v3.0 PR 7 — Closings tab for the Pool Detail page
// (design spec §8.2). Lists every investor_closing payment recorded against
// this pool. Each row expands to the detail view (subscriptions +
// disbursements), fetched lazily from /api/closings/{paymentId}.

import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { Badge, Button, Card } from "@/design-system/components/ui";
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import {
  useClosingDetail,
  usePoolClosings,
} from "@/hooks/capitalMarkets/useSpvPools";
import type { SpvPoolDto } from "@/types/spvPool";
import { formatCurrency, formatDate, humanizeStatus } from "../../shared/format";

export function ClosingsTab({ pool }: { pool: SpvPoolDto }) {
  const navigate = useNavigate();
  const closingsQuery = usePoolClosings(pool.id);
  const [expanded, setExpanded] = useState<string | null>(null);

  const rows = closingsQuery.data ?? [];

  const toggle = (paymentId: string) =>
    setExpanded((cur) => (cur === paymentId ? null : paymentId));

  return (
    <Card>
      <div className="flex items-center justify-between mb-space-3">
        <div>
          <h3 className="font-display font-bold text-h3 m-0">Closings</h3>
          <p className="text-small text-gray-mid m-0 mt-space-1">
            Each row is one investor_closing payment. Expand a row to see the
            subscriptions and WF2 disbursements written by the ceremony.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() =>
            navigate(`/capital-markets/pools/${pool.id}/closings/new`)
          }
        >
          <Plus size={14} aria-hidden /> Run New Closing
        </Button>
      </div>

      {closingsQuery.isLoading ? (
        <p className="text-small text-gray-mid italic m-0">
          Loading closings…
        </p>
      ) : rows.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          No closings have been recorded for this pool yet.
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH className="w-10"></TH>
              <TH>Closing Date</TH>
              <TH className="text-right">Gross Proceeds</TH>
              <TH className="text-right">Segments Triggered</TH>
              <TH className="text-right">SSNs Issued</TH>
              <TH className="text-right">Disbursement Total</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => {
              const isExpanded = expanded === row.paymentId;
              return (
                <Fragment key={row.paymentId}>
                  <TR
                    onClick={() => toggle(row.paymentId)}
                    className="cursor-pointer"
                  >
                    <TD>
                      {isExpanded ? (
                        <ChevronDown size={16} aria-hidden />
                      ) : (
                        <ChevronRight size={16} aria-hidden />
                      )}
                    </TD>
                    <TD>{formatDate(row.closingDate)}</TD>
                    <TD className="text-right">
                      {formatCurrency(row.grossProceeds)}
                    </TD>
                    <TD className="text-right">{row.segmentTriggerCount}</TD>
                    <TD className="text-right">{row.ssnsIssuedCount}</TD>
                    <TD className="text-right">
                      {formatCurrency(row.disbursementTotal)}
                    </TD>
                    <TD>
                      <Badge status={badgeStatusFor(row.status)}>
                        {humanizeStatus(row.status)}
                      </Badge>
                    </TD>
                  </TR>
                  {isExpanded && (
                    <TR className="bg-white-warm">
                      <TD colSpan={7}>
                        <ClosingDetail paymentId={row.paymentId} />
                      </TD>
                    </TR>
                  )}
                </Fragment>
              );
            })}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

function ClosingDetail({ paymentId }: { paymentId: string }) {
  const detailQuery = useClosingDetail(paymentId);

  if (detailQuery.isLoading) {
    return (
      <p className="text-small text-gray-mid italic m-0 py-space-2">
        Loading closing detail…
      </p>
    );
  }

  const detail = detailQuery.data;
  if (!detail) {
    return (
      <p className="text-small text-gray-mid italic m-0 py-space-2">
        Closing detail unavailable.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-space-3 py-space-2">
      {detail.notes && (
        <p className="text-caption text-gray-mid m-0">{detail.notes}</p>
      )}

      <div>
        <h5 className="font-display font-bold text-onyx m-0">
          Subscriptions ({detail.subscriptions.length})
        </h5>
        {detail.subscriptions.length === 0 ? (
          <p className="text-small text-gray-mid italic m-0 mt-space-1">
            No subscriptions on this closing.
          </p>
        ) : (
          <table className="w-full text-small mt-space-2">
            <thead>
              <tr className="text-left text-gold-shadow text-[10px] font-display uppercase tracking-label">
                <th className="py-space-1">Investor</th>
                <th className="py-space-1">Tier</th>
                <th className="py-space-1 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {detail.subscriptions.map((s, i) => (
                <tr
                  key={`${s.investorId}-${i}`}
                  className="border-t border-border-light"
                >
                  <td className="py-space-1">{s.investorDisplayName}</td>
                  <td className="py-space-1">{s.tier}</td>
                  <td className="py-space-1 text-right">
                    {formatCurrency(s.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <h5 className="font-display font-bold text-onyx m-0">
          Disbursements ({detail.disbursements.length})
        </h5>
        {detail.disbursements.length === 0 ? (
          <p className="text-small text-gray-mid italic m-0 mt-space-1">
            No disbursements on this closing.
          </p>
        ) : (
          <table className="w-full text-small mt-space-2">
            <thead>
              <tr className="text-left text-gold-shadow text-[10px] font-display uppercase tracking-label">
                <th className="py-space-1">Priority</th>
                <th className="py-space-1">Recipient</th>
                <th className="py-space-1">Obligation</th>
                <th className="py-space-1 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {detail.disbursements.map((d, i) => (
                <tr
                  key={`${d.priorityPosition}-${i}`}
                  className="border-t border-border-light"
                >
                  <td className="py-space-1">P{d.priorityPosition}</td>
                  <td className="py-space-1">
                    {d.recipientDisplayName ?? d.recipientResolution}
                  </td>
                  <td className="py-space-1">
                    <code className="text-caption">{d.obligationTypeCode}</code>
                  </td>
                  <td className="py-space-1 text-right">
                    {formatCurrency(d.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail.triggeredSegments.length > 0 && (
        <div className="text-caption text-gray-mid">
          Newly triggered segments:{" "}
          {detail.triggeredSegments.map((s) => `S${s}`).join(", ")}
        </div>
      )}
    </div>
  );
}

function badgeStatusFor(status: string) {
  const normalized = status.toLowerCase();
  if (normalized.includes("posted") || normalized.includes("complete"))
    return "finalized" as const;
  if (normalized.includes("pending") || normalized.includes("draft"))
    return "review" as const;
  if (normalized.includes("error") || normalized.includes("fail"))
    return "error" as const;
  return "draft" as const;
}
